/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { Grid, Row, Col } from 'react-flexbox-grid';
import { get, includes, uniq, uniqBy } from 'lodash';
import { ColumnType } from '@inqool/uas-web-core/es/components/controls/Grid/constants';

import {
  Date,
  DateField,
  InputField,
  Input,
  RemoteAutoCompleteField,
  RemoteAutoComplete,
  AutoCompleteField,
  AutoComplete,
  InlineGridField,
  InlineGrid,
  ChipInputField,
  ChipInput
} from '@components/form';
import { required } from '@utils/validators';
import Button from '@inqool/uas-web-core/es/components/controls/Button';

import { priorityEnum } from '@modules/tasks/_tasksEnums';
import { compose, lifecycle, withHandlers, withState } from 'recompose';
import { change, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { stringifyObject } from '@utils/utils';
import { withNotifications } from '@components/hoc/withNotifications';
import { getEventByNumber } from '@modules/events/_eventsActions';
import { getDelictsByEvent } from '@modules/delicts/_delictsActions';

const enhance = compose<any, any>(
  connect<any>(
    state => {
      return {
        events: formValueSelector('module-tasks')(state, 'events'),
        delicts: formValueSelector('module-tasks')(state, 'delicts')
      };
    },
    dispatch => ({
      changeEvents: value => dispatch(change('module-tasks', 'events', value)),
      changeDelicts: value => dispatch(change('module-tasks', 'delicts', value))
    })
  ),
  withNotifications({ getEventByNumber, getDelictsByEvent }),
  withState('eventSearchQuery', 'handleEventSearchQuery', ''),
  withState('disabledEventSearch', 'handleDisabledEventSearch', false),
  withState('availableDelicts', 'handleAvailableDelicts', []),
  lifecycle<any, {}, {}>({
    async componentDidMount() {
      let evts: any[] = [];
      const { events, delicts, changeDelicts, handleAvailableDelicts } = this.props;
      if (events) {
        evts = events;
      }
      const arr: any[] = [];
      for (const event of evts) {
        arr.push(...event.delicts);
      }
      const uniqeDelicts = uniqBy(arr, 'id');
      changeDelicts(delicts);
      handleAvailableDelicts(uniqeDelicts);
    }
  }),
  withHandlers({
    handleEventSearch: ({
      eventSearchQuery,
      handleDisabledEventSearch,
      getEventByNumberNotify,
      events,
      changeEvents,
      availableDelicts,
      handleAvailableDelicts
    }) => async () => {
      handleDisabledEventSearch(true);
      const res = await getEventByNumberNotify(eventSearchQuery);
      if (res === null || res.count === 0) {
        handleDisabledEventSearch(false);
        return;
      }
      const event = res.items[0];
      const newDelicts = uniqBy([...availableDelicts, ...event.delicts], 'id');
      handleAvailableDelicts(newDelicts);
      changeEvents([...events, event]);
      handleDisabledEventSearch(false);
    }
  })
);

const View: React.SFC<any> = ({
  handleEventSearch,
  handleEventSearchQuery,
  disabledEventSearch,
  availableDelicts,
  handleAvailableDelicts,
  delicts,
  changeDelicts
}) => (
  <div>
    <Grid fluid={true}>
      <Row>
        <Col className="grid-system-margin" />
      </Row>

      <Row>
        <Col md={12}>
          <Row>
            <Col md={6} className="with-right-divider">
              <Row>
                <Col md={12}>
                  <h4>Název</h4>
                </Col>
                <Col md={12}>
                  <InputField component={Input} id="name" name="name" label="Název *" validate={[required]} />
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <Row>
                <Col md={12}>
                  <h4>Datum od</h4>
                </Col>
                <Col md={12}>
                  <DateField component={Date} dateOnly={false} id="startDate" name="startDate" label="Datum od" />
                </Col>
                <Col md={12}>
                  <h4>Datum do</h4>
                </Col>
                <Col md={12}>
                  <DateField
                    component={Date}
                    dateOnly={false}
                    id="endDate"
                    name="endDate"
                    label="Datum do"
                    allowFuture={true}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <h4>Zadání</h4>
        </Col>
        <Col md={12}>
          <InputField
            component={Input}
            multiLine={true}
            name="instructions"
            label="Zadání"
            style={{ maxHeight: '60vh' }}
            inputStyle={{ maxHeight: '60vh' }}
            textareaStyle={{ maxHeight: '56vh' }}
          />
        </Col>

        <Col md={12}>
          <h4>Popis řešení</h4>
        </Col>
        <Col md={12}>
          <InputField
            component={Input}
            multiLine={true}
            name="solutionDescription"
            label="Popis řešení"
            style={{ maxHeight: '60vh' }}
            inputStyle={{ maxHeight: '60vh' }}
            textareaStyle={{ maxHeight: '56vh' }}
          />
        </Col>

        <Col md={12}>
          <h4>Události</h4>
        </Col>
        <Col md={12}>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <InputField
              name=""
              component={Input}
              onChange={e => handleEventSearchQuery(stringifyObject(e))}
              disabled={disabledEventSearch}
            />
            <Button
              label={'Vyhledat'}
              onClick={handleEventSearch}
              style={{ marginLeft: '10px' }}
              disabled={disabledEventSearch}
            />
          </div>
          <InlineGridField
            component={InlineGrid}
            name="events"
            columns={[{ label: 'Číslo události', field: 'number', type: ColumnType.TEXT, width: 80 }]}
            showCheckboxes={true}
            adding={false}
            deleting={true}
            editing={false}
            filtering={false}
            onDelete={data => {
              const deletedDelicts: any[] = [];
              for (const event of data) {
                deletedDelicts.push(...event.delicts);
              }
              const deletedDelictIds = uniq(deletedDelicts.map(d => d.id));
              const filteredAvailableDelicts = availableDelicts.filter(d => !includes(deletedDelictIds, d.id));
              handleAvailableDelicts(filteredAvailableDelicts);
              const filteredSelectedDelicts = delicts.filter(d => !includes(deletedDelictIds, d.id));
              changeDelicts(filteredSelectedDelicts);
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Row>
            <Col md={6} className="with-right-divider">
              <Row>
                <Col md={12}>
                  <h4>Vozidlo</h4>
                </Col>
                <Col md={12}>
                  <RemoteAutoCompleteField
                    component={RemoteAutoComplete}
                    id="serviceVehicle"
                    name="serviceVehicle"
                    label="Vozidlo"
                    url="/metropolis/api/cl/vehicles"
                    fullWidth={true}
                    labelFunction={o => get(o, 'fullName')}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <h4>Stav</h4>
                </Col>
                <Col md={12}>
                  <RemoteAutoCompleteField
                    component={RemoteAutoComplete}
                    id="type"
                    name="type"
                    label="Stav"
                    url="/metropolis/api/ts/task-states"
                    filterActive={true}
                    fullWidth={true}
                    labelFunction={o => get(o, 'name')}
                  />
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <Row>
                <Col md={12}>
                  <h4>Přestupky</h4>
                </Col>
                <Col md={12}>
                  <ChipInputField
                    component={ChipInput}
                    id="delicts"
                    name="delicts"
                    label="Přestupky"
                    items={availableDelicts}
                    labelFunction={o => get(o, 'number')}
                    valueFunction={o => o}
                    fullWidth={true}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <h4>Priorita</h4>
                </Col>
                <Col md={12}>
                  <AutoCompleteField
                    component={AutoComplete}
                    id="priority"
                    name="priority"
                    label="Priorita"
                    items={priorityEnum}
                    labelFunction={o => get(o, 'label')}
                    valueFunction={o => get(o, 'id')}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col className="grid-system-margin" />
      </Row>
    </Grid>
  </div>
);

export default enhance(View);



// WEBPACK FOOTER //
// ./src/modules/tasks/TasksFormTask.tsx